import { observer } from 'mobx-react';
import styled from 'styled-components';

// Components
import { RightOutlined } from '@ant-design/icons';
import ItemModifiers from 'components/checkout/ItemModifiers';
import { Button } from 'top-component-library';

// Hooks
import { useItemSheet } from 'hooks/use-item-sheet';
import CartModifier from 'models/CartModifier';
import { useEffect } from 'react';

/**
 * When a cartModifier is selected, this component displays:
 * (1) all of the cartModifier's selected nested modifiers, and
 * (2) a button that can be clicked to customize the cartModifier's nested modifiers.
 */
function ModifierDetails({ cartModifier }: { cartModifier: CartModifier }) {
  const { setDisplayedItem, addModWithMissingSelections, removeModWithMissingSelections } = useItemSheet();

  useEffect(() => {
    if (cartModifier.numMissingSelections > 0) {
      addModWithMissingSelections(cartModifier);
    } else {
      removeModWithMissingSelections(cartModifier);
    }
  }, [cartModifier, cartModifier.selected, addModWithMissingSelections, removeModWithMissingSelections]);

  return (
    <Body>
      <ItemModifiers item={cartModifier} />
      <ModifierButton onClick={() => setDisplayedItem(cartModifier)}>
        <Text>{cartModifier.getButtonText()}</Text>
        <Icon />
      </ModifierButton>
    </Body>
  );
}

export default observer(ModifierDetails);

const Body = styled.div`
  border-bottom: 1px solid var(--color-neutral__line);
  border-top: 1px solid var(--color-neutral__line);
  margin-left: 0.625rem;
  margin-top: 0.25rem;
  padding: 0;

  & > div:last-of-type {
    padding-bottom: 0.625rem;
  }
`;

const ModifierButton = styled(Button)`
  align-items: center;
  border: 0;
  display: flex;
  flex-direction: row;
  font-style: italic;
  height: 2rem;
  justify-content: space-between;
  letter-spacing: initial;
  padding: 0.5rem 0.5rem 0.5rem 2rem;
  text-transform: initial;
  width: 100%;

  &:focus,
  &:hover {
    opacity: 0.8;
  }
`;

const Icon = styled(RightOutlined)`
  && {
    color: var(--color-neutral__title-active);
    display: flex;
    justify-content: flex-end;
    width: 100%;
  }
`;

const Text = styled.span`
  && {
    align-items: center;
    color: var(--color-neutral__title-active);
    display: flex;
    font-size: 0.625rem;
    font-style: italic;
    font-weight: 400;
    line-height: 0.75rem;
  }
`;
